import { clsx } from 'clsx';
import type { Background } from '@character-creator/types';
import { useCharacterCreationStore } from '../../../stores/characterCreationStore';
import { useRulesetBackgrounds, useRulesetBackground } from '../../../hooks/useRulesetData';
import { formatSkill } from '../../../utils/formatting';
import { DiceIcon } from '../../ui/Icons';

export function BackgroundStep() {
  const { draft, setBackground, setBackstory, setConfirmedNoBackground, clearBackground } = useCharacterCreationStore();

  // Use ruleset-aware data
  const backgrounds = useRulesetBackgrounds();
  const selectedBackground = useRulesetBackground(draft.backgroundId);
  const hasNoBackground = draft.backgroundId === null && draft.confirmedNoBackground;

  const handleSuggest = () => {
    const randomBackground = backgrounds[Math.floor(Math.random() * backgrounds.length)];
    setBackground(randomBackground.id);
    setConfirmedNoBackground(false);
  };

  const hasData = draft.backgroundId !== null || draft.confirmedNoBackground || draft.backstory.trim() !== '';

  return (
    <div className="animate-fade-in">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-bold text-text-primary mb-2">
            Choose Your Background
          </h2>
          <p className="text-text-secondary">
            Your background reveals where you came from and how you became an adventurer.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasData && (
            <button
              type="button"
              onClick={clearBackground}
              className="flex-shrink-0 px-3 py-1.5 text-sm rounded-lg text-text-muted hover:text-error hover:bg-error/10 transition-colors"
            >
              Clear
            </button>
          )}
          <button
            type="button"
            onClick={handleSuggest}
            className="flex-shrink-0 px-3 py-1.5 text-sm rounded-lg bg-background-secondary text-text-primary hover:bg-background-elevated transition-colors flex items-center gap-1.5"
          >
            <DiceIcon className="w-4 h-4" />
            Suggest
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Background Selection */}
        <div>
          <h3 className="text-lg font-semibold text-text-primary mb-4">Backgrounds</h3>
          <div className="space-y-2">
            {/* None Option */}
            <button
              type="button"
              onClick={() => {
                setBackground(null);
                setConfirmedNoBackground(true);
              }}
              className={clsx(
                'w-full text-left p-4 rounded-lg border transition-all duration-200',
                hasNoBackground
                  ? 'bg-accent-gold/10 border-accent-gold'
                  : 'bg-background-card border-background-elevated hover:border-text-muted'
              )}
            >
              <div className="flex items-center justify-between">
                <span className={clsx(
                  'font-semibold',
                  hasNoBackground ? 'text-accent-gold' : 'text-text-primary'
                )}>
                  None
                </span>
                <span className="text-sm text-text-muted">No background</span>
              </div>
              <p className="text-sm text-text-secondary mt-1">
                Skip background selection. You won't receive background skills, tools, or features.
              </p>
            </button>

            {backgrounds.map((bg) => (
              <BackgroundCard
                key={bg.id}
                background={bg}
                isSelected={draft.backgroundId === bg.id}
                onClick={() => {
                  setBackground(bg.id);
                  setConfirmedNoBackground(false);
                }}
              />
            ))}
          </div>
          <p className="mt-4 text-sm text-text-muted">
            Note: Only the Acolyte background is included in the SRD 5.1.
          </p>
        </div>

        {/* Background Details */}
        <div>
          {selectedBackground ? (
            <BackgroundDetails background={selectedBackground} />
          ) : hasNoBackground ? (
            <div className="card p-6">
              <h3 className="font-display text-xl font-bold text-accent-gold mb-2">
                No Background
              </h3>
              <p className="text-text-secondary mb-4">
                You've chosen to skip background selection.
              </p>
              <div className="space-y-2 text-sm text-text-muted">
                <p>You will not receive:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Background skill proficiencies</li>
                  <li>Background tool proficiencies</li>
                  <li>Background languages</li>
                  <li>Starting equipment from background</li>
                  <li>Background feature</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="card p-6 text-center text-text-muted">
              <p>Select a background to see details</p>
            </div>
          )}
        </div>
      </div>

      {/* Backstory */}
      <div className="card p-6 mt-6">
        <h3 className="font-semibold text-text-primary mb-4">Backstory</h3>
        <textarea
          value={draft.backstory}
          onChange={(e) => setBackstory(e.target.value)}
          placeholder="Write your character's backstory..."
          className="input min-h-[150px] resize-y"
          maxLength={5000}
        />
        <p className="mt-2 text-sm text-text-muted">
          {draft.backstory.length}/5000 characters
        </p>
      </div>
    </div>
  );
}

interface BackgroundCardProps {
  background: Background;
  isSelected: boolean;
  onClick: () => void;
}

function BackgroundCard({ background, isSelected, onClick }: BackgroundCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'w-full text-left p-4 rounded-lg border transition-all duration-200',
        isSelected
          ? 'bg-accent-gold/10 border-accent-gold'
          : 'bg-background-card border-background-elevated hover:border-text-muted'
      )}
    >
      <div className="flex items-center justify-between">
        <span className={clsx(
          'font-semibold',
          isSelected ? 'text-accent-gold' : 'text-text-primary'
        )}>
          {background.name}
        </span>
        <span className="text-sm text-text-muted">
          {background.skillProficiencies.map(s => formatSkill(s)).join(', ')}
        </span>
      </div>
      <p className="text-sm text-text-secondary mt-1 line-clamp-2">
        {background.description}
      </p>
    </button>
  );
}

interface BackgroundDetailsProps {
  background: Background;
}

function BackgroundDetails({ background }: BackgroundDetailsProps) {
  return (
    <div className="card p-6 space-y-6 max-h-[600px] overflow-y-auto">
      <div>
        <h3 className="font-display text-xl font-bold text-accent-gold mb-2">
          {background.name}
        </h3>
        <p className="text-text-secondary">{background.description}</p>
      </div>

      {/* Proficiencies */}
      <div className="space-y-3 text-sm">
        <div>
          <span className="text-text-muted">Skill Proficiencies:</span>
          <span className="ml-2 text-text-primary">
            {background.skillProficiencies.map(s => formatSkill(s)).join(', ')}
          </span>
        </div>
        {background.toolProficiencies.length > 0 && (
          <div>
            <span className="text-text-muted">Tool Proficiencies:</span>
            <span className="ml-2 text-text-primary">
              {background.toolProficiencies.join(', ')}
            </span>
          </div>
        )}
        {background.languages > 0 && (
          <div>
            <span className="text-text-muted">Languages:</span>
            <span className="ml-2 text-text-primary">
              {background.languages} of your choice
            </span>
          </div>
        )}
      </div>

      {/* Equipment */}
      <div>
        <h4 className="text-sm font-semibold text-text-primary mb-2">Equipment</h4>
        <ul className="text-sm text-text-secondary space-y-1">
          {background.equipment.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      </div>

      {/* Feature */}
      <div>
        <h4 className="text-sm font-semibold text-text-primary mb-2">
          Feature: {background.feature.name}
        </h4>
        <p className="text-sm text-text-secondary">{background.feature.description}</p>
      </div>

      {/* Suggested Characteristics */}
      <div className="border-t border-background-elevated pt-4">
        <h4 className="text-sm font-semibold text-text-primary mb-3">
          Suggested Characteristics
        </h4>

        <CharacteristicSection
          title="Personality Traits"
          items={background.suggestedCharacteristics.personalityTraits}
        />
        <CharacteristicSection
          title="Ideals"
          items={background.suggestedCharacteristics.ideals}
        />
        <CharacteristicSection
          title="Bonds"
          items={background.suggestedCharacteristics.bonds}
        />
        <CharacteristicSection
          title="Flaws"
          items={background.suggestedCharacteristics.flaws}
        />
      </div>
    </div>
  );
}

interface CharacteristicSectionProps {
  title: string;
  items: string[];
}

function CharacteristicSection({ title, items }: CharacteristicSectionProps) {
  return (
    <details className="mb-3">
      <summary className="text-sm font-medium text-text-primary cursor-pointer hover:text-accent-gold">
        {title} ({items.length})
      </summary>
      <ol className="mt-2 ml-4 text-sm text-text-secondary space-y-1 list-decimal">
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ol>
    </details>
  );
}


